import { FC, useEffect, useMemo, useState } from "react";
import { calendarService, ICalendarFilter, IGetCalendarFilterOptionsResponse } from "../../../entities/calendar";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { ITutorData } from "../../../entities/tutor";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { LoaderSpinner } from "../../../shared/ui/spinner";
import { CalendarDay } from "../../../widgets/calendarDay";
import { CalendarMonth } from "../../../widgets/calendarMonth";
import classes from './tutorLessonsCalendar.module.scss'

interface IProps {
    tutor: ITutorData;
    tutorUserId: number;
    hideTitle?: boolean;
}

const getTgAccountTitle = (title: string, username: string): string => {
    const value = title || username

    if(!value) return ''

    return value.startsWith('@') ? value : `@${value}`
}

export const TutorLessonsCalendar: FC<IProps> = ({tutor, tutorUserId, hideTitle = false}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const [filterOptions, setFilterOptions] = useState<IGetCalendarFilterOptionsResponse | null>(null)
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')

    const tutorUserIdFilter = String(tutorUserId || tutor.user_id || '')
    const selectedTutorFilter = useMemo<ICalendarFilter>(() => ({
        tgAccountIds: [],
        userIds: tutorUserIdFilter ? [tutorUserIdFilter] : [],
        studentIds: [],
        tagIds: [],
        includeCancelled: true,
        includeRescheduled: true,
    }), [tutorUserIdFilter])

    useEffect(() => {
        if(!tutorUserIdFilter){
            setFilterOptions(null)
            return
        }

        const controller = new AbortController()

        const getFilterOptions = async () => {
            try{
                setIsLoading(true)
                setError('')
                setSelectedDate(null)

                const response = await calendarService.getCalendarFilterOptions(
                    {selected: selectedTutorFilter},
                    controller.signal,
                )

                if(!controller.signal.aborted){
                    setFilterOptions(response)
                }
            }
            catch(error){
                if(error instanceof DOMException && error.name === 'AbortError') return

                console.log(error)
                setFilterOptions(null)

                if(error instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else if(error instanceof MyError){
                    setError(error.message)
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else{
                    setError('Ошибка при получении данных календаря')
                    setGlobalMessage({message: 'Ошибка при получении данных календаря', type: 'error'})
                }
            }
            finally{
                if(!controller.signal.aborted){
                    setIsLoading(false)
                }
            }
        }

        getFilterOptions()

        return () => controller.abort()
    }, [selectedTutorFilter, setGlobalMessage, setIsAuth, tutorUserIdFilter])

    const filter = selectedTutorFilter

    const tgAccountNames = (filterOptions?.tgAccounts || [])
        .map(tgAccount => getTgAccountTitle(tgAccount.title, tgAccount.username))
        .filter(Boolean)
        .join(', ')

    const hasCalendarData = Boolean(filter.userIds.length)

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <section>
                    {!hideTitle && <h2 className={classes.title}>Календарь занятий</h2>}
                    <p className={classes.subtitle}>
                        {tutor.full_name}
                        {tgAccountNames ? ` · ${tgAccountNames}` : ''}
                        {' · время по МСК'}
                    </p>
                </section>
            </section>

            {isLoading && <section className={classes.loader}><LoaderSpinner /></section>}

            {!isLoading && error && <p className={classes.message}>{error}</p>}

            {!isLoading && !error && !tutorUserIdFilter && (
                <p className={classes.message}>Для календаря не указан исполнитель.</p>
            )}

            {!isLoading && !error && filterOptions && !hasCalendarData && (
                <p className={classes.message}>Для календаря не указан исполнитель.</p>
            )}

            {!isLoading && !error && filterOptions && hasCalendarData && (
                selectedDate
                    ? <CalendarDay
                        date={selectedDate}
                        filter={filter}
                        filterOptions={filterOptions}
                        backTitle="К месяцу"
                        onBack={() => setSelectedDate(null)}
                    />
                    : <CalendarMonth
                        filter={filter}
                        onSelectDate={setSelectedDate}
                    />
            )}
        </section>
    )
}
